// Общий redirect-based OAuth-флоу (Google, Telegram OIDC) - обе "живут" на
// бэкенде почти идентично (GET .../start -> редирект на провайдера ->
// провайдер редиректит на фиксированную страницу фронтенда -> та вызывает
// POST .../callback), различаются только путями. См.
// backend/app/auth/router.py (/auth/google/*, /auth/telegram/oidc/*) и
// docs/technical/api-reference.md.
import api from "../api";

export type OAuthProvider = "google" | "telegram";

interface ProviderConfig {
  startPath: string;
  callbackPath: string;
  // Страница ЭТОГО фронтенда, на которую провайдер возвращает браузер -
  // должна быть зарегистрирована как Allowed URL/Authorized redirect URI
  // именно с этим путём (см. CLAUDE.local.md).
  frontendRedirectPath: string;
}

const PROVIDERS: Record<OAuthProvider, ProviderConfig> = {
  google: {
    startPath: "/auth/google/start",
    callbackPath: "/auth/google/callback",
    frontendRedirectPath: "/auth/google/callback",
  },
  telegram: {
    startPath: "/auth/telegram/oidc/start",
    callbackPath: "/auth/telegram/oidc/callback",
    frontendRedirectPath: "/auth/telegram/callback",
  },
};

// Переживает redirect на чужой домен и обратно (localStorage/sessionStorage,
// не Pinia state) - CredentialConflictCard.vue кладёт сюда link_token перед
// тем, как увести пользователя логиниться заново через Google/Telegram;
// OAuthCallbackView.vue подхватывает и потребляет его после успешного входа.
export const PENDING_CLAIM_LINK_TOKEN_KEY = "eco_pending_claim_link_token";

// Привязка входа к браузеру (защита от login CSRF): случайный nonce живёт
// только в localStorage браузера, начавшего вход; на бэкенд уходит в /start
// (хеш попадает в подписанный state) и в /callback (сверка). Чужая ссылка
// callback?code=&state= из другого браузера не пройдёт - nonce не совпадёт.
const BROWSER_NONCE_KEY = "eco_oauth_browser_nonce";

function newBrowserNonce(): string {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  const nonce = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  try {
    localStorage.setItem(BROWSER_NONCE_KEY, nonce);
  } catch {
    // без localStorage вход не завершится (callback не найдёт nonce) - осознанно
  }
  return nonce;
}

function readBrowserNonce(): string {
  try {
    return localStorage.getItem(BROWSER_NONCE_KEY) ?? "";
  } catch {
    return "";
  }
}

function clearBrowserNonce(): void {
  try {
    localStorage.removeItem(BROWSER_NONCE_KEY);
  } catch {
    // ничего
  }
}

export function redirectUriFor(provider: OAuthProvider): string {
  return `${window.location.origin}${PROVIDERS[provider].frontendRedirectPath}`;
}

// Полный переход на страницу авторизации провайдера (не popup - без
// бэкендовой сессии/cookie нет смысла усложнять postMessage-обменом между
// окнами, а localStorage-токены и так переживают полный переход по ссылке
// и обратно).
export async function startOAuthLogin(
  provider: OAuthProvider,
  intent: "login" | "link" = "login"
): Promise<void> {
  const config = PROVIDERS[provider];
  const res = await api.get(config.startPath, {
    params: { redirect_uri: redirectUriFor(provider), intent, browser_nonce: newBrowserNonce() },
  });
  window.location.href = res.data.url;
}

export interface OAuthCallbackResult {
  status: "logged_in" | "linked";
  tokens?: { access_token: string; refresh_token: string };
  identity?: { provider: string; username: string | null };
}

export async function completeOAuthCallback(
  provider: OAuthProvider,
  code: string,
  state: string
): Promise<OAuthCallbackResult> {
  const config = PROVIDERS[provider];
  try {
    const res = await api.post(config.callbackPath, {
      code,
      state,
      redirect_uri: redirectUriFor(provider),
      browser_nonce: readBrowserNonce(),
    });
    return res.data;
  } finally {
    clearBrowserNonce();
  }
}
